// Local-mode mocks: [{ pattern, respond(body) }], first match wins.
// Database agents (direct-access badge) and install credentials (db.env).
import { MOCK_HTTP_STATUS } from "./dbPolicyMocks";

const ago = (ms) => new Date(Date.now() - ms).toISOString();
const MIN = 60 * 1000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

const EXPOSED_DETAIL = [
  "Direct access check: EXPOSED (2 findings)",
  "",
  "1. ProxySQL admin interface is reachable from the network",
  "   admin-mysql_ifaces = 0.0.0.0:6032",
  "   Port 6032 accepted a connection from 10.20.4.17 (not loopback).",
  "",
  "2. ProxySQL admin still uses the default password",
  "   Logged in to 127.0.0.1:6032 as admin with password \"admin\".",
  "",
  "Whoever can log in here can read every password AuthNull manages on this host.",
  "",
  "Fix (run on this host, in the ProxySQL admin shell: mysql -u admin -p -h 127.0.0.1 -P 6032):",
  "  UPDATE global_variables SET variable_value='admin:<PROXYSQL_ADMIN_PASSWORD from db.env>'",
  "    WHERE variable_name='admin-admin_credentials';",
  "  UPDATE global_variables SET variable_value='127.0.0.1:6032'",
  "    WHERE variable_name='admin-mysql_ifaces';",
  "  LOAD ADMIN VARIABLES TO RUNTIME;",
  "  SAVE ADMIN VARIABLES TO DISK;",
  "",
  "Then restart the agent: sudo systemctl restart authnull-db-agent",
].join("\n");

const PROTECTED_DETAIL = [
  "Direct access check: PROTECTED",
  "",
  "PostgreSQL 5432: accepts connections only from the AuthNull proxy (pg_hba.conf: host all all 127.0.0.1/32 scram-sha-256).",
  "ProxySQL admin: bound to 127.0.0.1:6032; default password rejected.",
  "No findings.",
].join("\n");

const AGENT_HOSTS = () => [
  {
    agentHostAddress: "192.168.122.175",
    hostName: "pg-prod-01",
    agentStatus: "Active",
    directAccessStatus: "protected",
    directAccessDetail: PROTECTED_DETAIL,
    directAccessCheckedAt: ago(2 * MIN),
  },
  {
    agentHostAddress: "192.168.122.15",
    hostName: "mysql-billing-02",
    agentStatus: "Active",
    directAccessStatus: "exposed",
    directAccessDetail: EXPOSED_DETAIL,
    directAccessCheckedAt: ago(7 * MIN),
  },
  {
    agentHostAddress: "10.0.4.31",
    hostName: "pg-analytics",
    agentStatus: "Inactive",
    // An agent that has not reported a check yet: no status, detail or time.
  },
];

const matchesFilters = (host, filters = []) =>
  (Array.isArray(filters) ? filters : []).every(({ filterParameter, filterValue }) => {
    const v = String(filterValue || "").toLowerCase();
    if (!v) return true;
    if (filterParameter === "host_name") return String(host.hostName).toLowerCase().includes(v);
    if (filterParameter === "status") return String(host.agentStatus).toLowerCase() === v;
    if (filterParameter === "agent_host_ip") return String(host.agentHostAddress).toLowerCase().includes(v);
    return true;
  });

// Install credentials, kept for the session. Secrets are returned by issue
// only and are not stored here.
let nextId = 4;
const credentials = [
  { id: 3, tenantId: 1, label: "Linux postgres pg-prod-01", clientId: "dpc-7f3a9c21e4b8", createdAt: ago(2 * DAY), createdBy: 31, lastUsedAt: ago(3 * MIN) },
  { id: 2, tenantId: 1, label: "Linux mysql mysql-billing-02", clientId: "dpc-1b6d04ae93f2", createdAt: ago(9 * DAY), createdBy: 31, lastUsedAt: ago(8 * MIN) },
  { id: 1, tenantId: 1, label: "Old staging box", clientId: "dpc-c2e85f7a0d19", createdAt: ago(40 * DAY), createdBy: 31, lastUsedAt: ago(32 * DAY), revokedAt: ago(30 * DAY) },
];

const hex = (n) => Array.from({ length: n }, () => Math.floor(Math.random() * 16).toString(16)).join("");

export const DB_AGENT_MOCKS = [
  {
    pattern: "getdatabaseagenthost",
    respond: (body) => {
      const all = AGENT_HOSTS().filter((h) => matchesFilters(h, body?.filters));
      const limit = Number(body?.limit) || 10;
      const page = Math.max(1, Number(body?.page_id) || 1);
      return {
        code: "200",
        status: "success",
        message: "ok",
        agentHostData: all.slice((page - 1) * limit, page * limit),
        count: all.length,
      };
    },
  },
  {
    pattern: "/proxy/credentials/list",
    respond: () => ({ data: credentials.map((c) => ({ ...c })) }),
  },
  {
    pattern: "/proxy/credentials/revoke",
    respond: (body) => {
      const id = Number(body?.credentialId);
      const row = credentials.find((c) => c.id === id);
      if (!row) return { [MOCK_HTTP_STATUS]: 404, error: "no such credential" };
      if (!row.revokedAt) row.revokedAt = new Date().toISOString();
      return { revoked: id };
    },
  },
  {
    pattern: /\/proxy\/credentials\/?(\?.*)?$/,
    respond: (body) => {
      const clientId = `dpc-${hex(12)}`;
      credentials.unshift({
        id: nextId++,
        tenantId: Number(body?.tenantId) || 1,
        label: String(body?.label || ""),
        clientId,
        createdAt: new Date().toISOString(),
        createdBy: 31,
      });
      return { clientId, clientSecret: `dps_${hex(40)}`, tokenPath: "/api/v1/database/proxy/token" };
    },
  },
];
